import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GRADE_ORDER = [
  'Primary 1',
  'Primary 2',
  'Primary 3',
  'Primary 4',
  'Primary 5',
  'Primary 6',
];

const PromoteAllDialog = ({ classroom, studentCount, onClose, onConfirm }) => {
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState(null);

  const currentGrade = classroom?.gradeLevel;
  const currentIndex = GRADE_ORDER?.indexOf(currentGrade);
  const isMaxGrade = currentIndex === GRADE_ORDER?.length - 1 || currentIndex === -1;
  const nextGrade = !isMaxGrade ? GRADE_ORDER?.[currentIndex + 1] : null;

  const handleConfirm = async () => {
    try {
      setPromoting(true);
      setError(null);
      await onConfirm(nextGrade);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to promote students');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isMaxGrade ? 'bg-warning/10' : 'bg-primary/10'
            }`}>
              <Icon
                name={isMaxGrade ? 'AlertCircle' : 'ArrowUpCircle'}
                size={20}
                className={isMaxGrade ? 'text-warning' : 'text-primary'}
              />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Promote All Students</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          {isMaxGrade ? (
            <div className="text-center py-4">
              <p className="text-base text-foreground font-medium mb-2">
                Highest Grade Reached
              </p>
              <p className="text-sm text-muted-foreground">
                These students are already in <strong>{currentGrade}</strong>, the highest grade level. No further promotion is available.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-foreground mb-4">
                Promote all <strong>{studentCount}</strong> student{studentCount !== 1 ? 's' : ''} from{' '}
                <strong>{currentGrade}</strong> to <strong>{nextGrade}</strong>?
              </p>
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                      {currentGrade}
                    </span>
                    <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
                      {nextGrade}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  This will update the classroom grade level and all enrolled students' grade.
                </p>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {isMaxGrade ? 'Close' : 'Cancel'}
          </Button>
          {!isMaxGrade && (
            <Button
              onClick={handleConfirm}
              disabled={promoting}
              iconName={promoting ? 'Loader2' : 'ArrowUpCircle'}
              className="flex-1"
            >
              {promoting ? 'Promoting...' : 'Confirm Promote'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoteAllDialog;
